import { type MivoTranslationKey } from "./ITranslation";
import {
  DEFAULT_MARKET_COUNTRY,
  type MarketCountry,
  defaultLocaleForMarketCountry,
} from "./marketCountry";
import { writeStoredMarketCountry } from "./marketCountryStorage";
import { applyLocale } from "./useLocale";

export const MARKET_COUNTRY_SWITCHER_TRANSLATIONS = {
  label: {
    "pt-BR": "País do mercado",
    en: "Market country",
    es: "País del mercado",
    de: "Marktland",
  },
  filterPlaceholder: {
    "pt-BR": "Buscar país",
    en: "Search country",
    es: "Buscar país",
    de: "Land suchen",
  },
  empty: {
    "pt-BR": "Nenhum resultado encontrado",
    en: "No results found",
    es: "No se encontraron resultados",
    de: "Keine Ergebnisse gefunden",
  },
} as const;

export function shouldOfferLocaleSwitch(
  locale: MivoTranslationKey,
  nextCountry: MarketCountry,
): boolean {
  return defaultLocaleForMarketCountry(nextCountry) !== locale;
}

export type CommitMarketCountrySelectionOptions = {
  nextCountry: MarketCountry;
  previousCountry?: MarketCountry;
  currentLocale: MivoTranslationKey;
  onPersistMarketCountry?: (marketCountry: MarketCountry) => Promise<void>;
  onPersistLocale?: (locale: MivoTranslationKey) => Promise<void>;
};

/**
 * Writes market-country storage and always applies the country default locale
 * when it differs from the current locale. Never prompts.
 */
export async function commitMarketCountrySelection({
  nextCountry,
  previousCountry = DEFAULT_MARKET_COUNTRY,
  currentLocale,
  onPersistMarketCountry,
  onPersistLocale,
}: CommitMarketCountrySelectionOptions): Promise<"unchanged" | "country" | "country-and-locale"> {
  if (nextCountry === previousCountry) {
    return "unchanged";
  }

  writeStoredMarketCountry(nextCountry);

  if (onPersistMarketCountry) {
    await onPersistMarketCountry(nextCountry);
  }

  if (!shouldOfferLocaleSwitch(currentLocale, nextCountry)) {
    return "country";
  }

  const nextLocale = defaultLocaleForMarketCountry(nextCountry);
  await applyLocale(nextLocale, {
    persistRemote: onPersistLocale ? () => onPersistLocale(nextLocale) : undefined,
  });

  return "country-and-locale";
}
